import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import Table from '../../../../../components/Table';
import rbacCheck from '../../../../../rbac/rbacCheck';
import { fetchProducts } from '../../../../../redux/safescarf/products/actions';
import { fetchProductTypes } from '../../../../../redux/safescarf/productTypes/actions';

import CreateProductFormModal from './CreateProductFormModal';

import { useUserData } from '@/redux/selectors';
import type { RootState } from '@/redux/store';
import type { TableField, TableRowData } from '@/types/tableTypes';

interface SafeScarfProduct {
  id: string | number;
  name: string;
  description: string;
  prod_type: string | number;
  [key: string]: unknown;
}

interface SafeScarfProductType {
  id: string | number;
  name: string;
  [key: string]: unknown;
}

const Products: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userData = useUserData();
  const products = useSelector(
    (state: RootState) => state.safescarf.products
  ) as SafeScarfProduct[];
  const productTypes = useSelector(
    (state: RootState) => state.safescarf.productTypes
  ) as SafeScarfProductType[];
  const fetchError = useSelector((state: RootState) => state.status.error);

  const [createModalOpened, setCreateModalOpened] = useState<boolean>(false);

  const canCreateProduct = rbacCheck(userData.roles, '/products/safescarf/products:POST');

  useEffect(() => {
    if (!products.length) dispatch(fetchProducts());
    if (!productTypes.length) dispatch(fetchProductTypes());
  }, [products.length, productTypes.length, dispatch]);

  const productTypeLookup = Object.fromEntries(
    productTypes.map(productType => [String(productType.id), productType.name])
  ) as Record<string, string>;

  const fields: TableField[] = [
    { title: 'Name', id: 'name' },
    { title: 'Description', id: 'description' },
    { title: 'Product type', id: 'prod_type' },
  ];

  const rows: TableRowData[] = products.map(product => ({
    key: String(product.id),
    onRowClick: () => navigate(String(product.id)),
    data: [
      { field: 'name', value: product.name },
      { field: 'description', value: product.description },
      {
        field: 'prod_type',
        value: productTypeLookup[String(product.prod_type)] ?? 'Unknown',
      },
    ],
  }));

  return (
    <>
      {createModalOpened && (
        <CreateProductFormModal
          onSubmit={() => {
            setCreateModalOpened(false);
          }}
          onCancel={() => {
            setCreateModalOpened(false);
          }}
        />
      )}
      <Table
        header='Products'
        fields={fields}
        rows={rows}
        error={fetchError}
        createAction={{
          disabled: !canCreateProduct,
          onClick: () => {
            setCreateModalOpened(true);
          },
          tooltip: !canCreateProduct ? intl.formatMessage({ id: 'createProductDisabled' }) : '',
        }}
      />
    </>
  );
};

export default Products;
